'use client';

import { motion } from 'framer-motion';
import Link from 'next/link';
import { ProductOverviewSection } from '@/types/sanity';
import { CTAButton } from '@/components/cta/CTAButton';
import { PortableText } from '@portabletext/react';
import { SectionHeader } from './SectionHeader';
import { SectionCTAs } from './SectionCTAs';
import Image from 'next/image';
import { urlFor } from '@/sanity/lib/image';
import {
  ScrollReveal,
  StaggerContainer,
  staggerItemVariants,
} from '@/components/animations/ScrollAnimations';

import { cn } from '@/lib/utils';
import { CTA } from '@/types/sanity';

function getProductHref(ctas?: CTA[]): string | undefined {
  if (!ctas?.length) return undefined;
  const cta = ctas[0];
  if (cta.actionType === 'external') return cta.externalLink;
  if (cta.actionType === 'link') return cta.internalLink;
  return undefined;
}

interface ProductOverviewSectionComponentProps {
  section: ProductOverviewSection;
  pageSlug?: string;
}

export function ProductOverviewSectionComponent({
  section,
  pageSlug,
}: ProductOverviewSectionComponentProps) {
  const layout = section.layout || 'cards-3';
  const centerAligned = pageSlug === 'audio';

  const getGridClass = () => {
    switch (layout) {
      case 'cards-2':
        return 'grid grid-cols-1 md:grid-cols-2 gap-12 max-w-3xl mx-auto';
      case 'cards-3':
        return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 max-w-6xl mx-auto';
      case 'cards-4':
        return 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 max-w-6xl mx-auto';
      case 'list-images':
        return 'space-y-12 max-w-4xl mx-auto';
      default:
        return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 max-w-6xl mx-auto';
    }
  };

  return (
    <ScrollReveal direction="up" delay={0.1}>
      <section
        className={cn(
          'product-overview-section py-8 md:py-12 lg:py-16',
          pageSlug && `product-overview-section--${pageSlug}`
        )}
      >
        <div className="mx-auto max-w-[90rem] sm:px-6 lg:px-8">
          <div className="px-6 sm:px-0">
            <SectionHeader
              title={section.title}
              subtitle={section.subtitle}
              description={section.description}
            />

            <StaggerContainer staggerDelay={0.15} className={getGridClass()}>
              {section.productLinks?.filter(Boolean).map((product) => {
                const href = getProductHref(product.ctas);

                return (
                  <motion.div
                    key={product._id}
                    variants={staggerItemVariants}
                    className={cn(
                      'flex flex-col',
                      centerAligned ? 'items-center text-center' : 'text-left'
                    )}
                  >
                    {/* Product image */}
                    <div className="self-center">
                      <ProductImage product={product} href={href} />
                    </div>

                    {/* Title & subtitle */}
                    <div className="mt-6 mb-4">
                      <h3 className="text-lg font-medium">{product.title}</h3>
                      {product.subtitle && (
                        <p className="text-sm text-muted-foreground mt-1">{product.subtitle}</p>
                      )}
                    </div>

                    {/* Features list */}
                    {product.description && (
                      <div
                        className={cn(
                          'prose prose-sm max-w-none mb-6 w-full [&_ul]:list-disc [&_ul]:pl-5 [&_li]:text-muted-foreground',
                          !centerAligned && 'text-left'
                        )}
                      >
                        <PortableText value={product.description} />
                      </div>
                    )}

                    {/* CTA */}
                    {product.ctas && product.ctas.length > 0 && (
                      <div className="mt-auto pt-2">
                        <CTAButton cta={product.ctas[0]} size="default" />
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </StaggerContainer>

            <SectionCTAs ctas={section.ctas} justify="start" />
          </div>
        </div>
      </section>
    </ScrollReveal>
  );
}

function ProductImage({
  product,
  href,
}: {
  product: { title: string; image?: { asset: { _ref: string; _type: 'reference' }; alt?: string } };
  href?: string;
}) {
  const circle = (
    <div className="w-36 h-36 md:w-50 md:h-50 rounded-full border border-dashed border-primary p-2 md:p-2.5 transition-transform duration-300 hover:scale-105 hover:shadow-lg">
      <div className="relative w-full h-full rounded-full bg-primary/10 overflow-hidden">
        {product.image?.asset ? (
          <Image
            src={urlFor(product.image).width(400).height(400).quality(85).url()}
            alt={product.image?.alt || product.title}
            fill
            sizes="200px"
            className="object-cover"
          />
        ) : (
          <div className="flex items-center justify-center h-full text-primary font-medium text-sm">
            {product.title}
          </div>
        )}
      </div>
    </div>
  );

  if (href) {
    return <Link href={href}>{circle}</Link>;
  }

  return circle;
}
